import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

import { updateSnackbar } from "../../_redux/SnackbarSlice";
import {
  fetchTemplates,
  fetchWindow,
  fetchMessages,
  fetchPhoneNumbers,
  markRead,
  sendText,
  sendTemplate,
  getErrorMessage,
} from "../../_redux/_apiHelpers/whatsappApi";
import { parsePhone } from "../../_utilities/whatsappContext";
import { resolveCountryIso } from "../../_utilities/whatsappCountries";
import { decrypt } from "../../_utilities/_encryptDecryptHelper";
import {
  buildRuntimeBody,
  extractPlaceholders,
  getBodyText,
} from "./templateMapper";
import "./WhatsAppConversation.css";

const COUNTDOWN_TICK_MS = 1000;
const initialSnackbar = { type: "", message: "" };

const formatRemaining = (ms) => {
  if (!ms || ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}h ${m}m ${s}s`;
};

const initials = (name) => {
  if (!name) return "";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

const WhatsAppConversation = ({ applicantDetails, integration }) => {
  const dispatch = useDispatch();

  const integrationId = integration?.id;

  // Phone is read-only and bound to the applicant whose profile the
  // drawer was opened from. We deliberately do NOT let the agent type a
  // different number here: doing so would (a) leak another candidate's
  // chat history into a profile it doesn't belong to, and (b) let an
  // agent send a WhatsApp message that gets logged against the wrong
  // applicant. If the stored contactNumber is invalid we show an inline
  // error and disable Send — the agent has to fix it via the applicant
  // edit flow, which is the right place for that change to be audited.
  const rawApplicantPhone = useMemo(() => {
    const raw = String(applicantDetails?.contactNumber || "").trim();
    if (!raw) return "";
    const startsWithPlus = raw.startsWith("+");
    const digits = raw.replace(/\D/g, "");
    if (!digits) return "";
    return startsWithPlus ? `+${digits}` : digits;
  }, [applicantDetails?.contactNumber]);

  // Default region for parsing local-format numbers — taken from the
  // agent's currently-selected country (top-right country switcher).
  // Stored in localStorage/sessionStorage as an encrypted JSON object via
  // _encryptDecryptHelper. If the cookie is missing/unrecognised we just
  // fall back to "no default" — libphonenumber still works for `+`-prefixed
  // input.
  const agentCountryIso = useMemo(() => {
    try {
      const raw = decrypt("selectCountry");
      if (!raw) return undefined;
      const obj = JSON.parse(raw);
      return resolveCountryIso(
        obj?.name || obj?.countryName || obj?.country || obj?.code,
      );
    } catch {
      return undefined;
    }
  }, []);

  const phoneInfo = useMemo(
    () => parsePhone(rawApplicantPhone, agentCountryIso),
    [rawApplicantPhone, agentCountryIso],
  );
  const phoneE164 = phoneInfo.valid ? phoneInfo.e164 : null;

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [samples, setSamples] = useState({});
  const [composerText, setComposerText] = useState("");
  const [messages, setMessages] = useState([]);
  const [windowState, setWindowState] = useState({
    open: false,
    reason: "loading",
    remainingMs: 0,
  });
  const [remainingMs, setRemainingMs] = useState(0);
  const [sending, setSending] = useState(false);
  const [showTemplatePickerInOpen, setShowTemplatePickerInOpen] =
    useState(false);
  // Country name (e.g. "India", "USA") of the integration's first active
  // WhatsApp business phone. Used as the SSE channel `countryId` — the
  // backend keys channels by this exact column value (whatsapp_phone_numbers.country),
  // not by numeric ids and not by the agent's selected country.
  const [integrationCountry, setIntegrationCountry] = useState("");

  // Countdown ticker (local-only). Server push uses SSE — see effect below.
  const tickRef = useRef(null);

  // Keep latest phone in a ref so the SSE handler (registered once per
  // integration+country) can read the current value to filter events
  // without forcing a reconnect when the user edits the phone.
  const phoneRef = useRef(null);

  // --- derived state ---

  const approvedTemplates = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates],
  );

  const selectedTemplate = useMemo(
    () =>
      approvedTemplates.find((t) => String(t.id) === String(selectedTemplateId)),
    [approvedTemplates, selectedTemplateId],
  );

  const selectedPlaceholders = useMemo(
    () => (selectedTemplate ? extractPlaceholders(getBodyText(selectedTemplate)) : []),
    [selectedTemplate],
  );

  const hasOutbound = messages.some((m) => m.direction === "outbound");
  const hasInbound = messages.some((m) => m.direction === "inbound");

  // Build the display text for a message bubble.
  //
  // For outbound template messages the backend returns `text` as just the
  // tag — e.g. `[Template: consent_confirmation]` — without the resolved
  // body. We enrich the bubble by:
  //   - keeping the [Template: name] tag inline as a separate styled span
  //   - looking up the matching template in our local `templates` cache and
  //     appending its body (placeholders kept as-is when we don't have a
  //     substituted version on the message itself).
  const messageDisplay = (msg) => {
    const raw =
      msg.text || msg.body || msg.displayText || msg.display_text || "";
    if (msg.direction !== "outbound" || msg.type !== "template") {
      return { tag: null, body: raw };
    }
    // Extract the template name from "[Template: <name>]" if present.
    const match = /\[Template:\s*([^\]]+)\]\s*(.*)/s.exec(raw);
    const name = match?.[1]?.trim() || msg.template_name || "";
    const trailing = match?.[2]?.trim() || "";
    let body = trailing;
    if (!body && name) {
      const tpl = templates.find((t) => t.name === name);
      body = tpl ? getBodyText(tpl) : "";
    }
    return { tag: name ? `[Template: ${name}]` : null, body };
  };

  const mode = useMemo(() => {
    if (messages.length === 0) return "idle";
    if (hasOutbound && !hasInbound) return "waiting";
    if (windowState.open) return "open";
    return "closed";
  }, [messages.length, hasOutbound, hasInbound, windowState.open]);

  // --- API helpers (stable references for polling) ---

  const refresh = useCallback(async (shouldAbort = () => false) => {
    if (!integrationId || !phoneE164) return;
    // Fetch independently so a backend 500 on one endpoint doesn't blow
    // away the other's data (and any optimistically-appended messages
    // stay intact when /messages fails). `shouldAbort` lets the caller
    // skip the setState dance if the component unmounted (or the phone
    // changed) while the request was in flight.
    try {
      const w = await fetchWindow(integrationId, phoneE164);
      if (shouldAbort()) return;
      setWindowState(w || { open: false, reason: "unknown" });
      setRemainingMs(w?.remainingMs || 0);
    } catch (error) {
      console.error("Whatsapp window fetch failed:", error);
    }
    try {
      const msgs = await fetchMessages(integrationId, phoneE164);
      if (shouldAbort()) return;
      if (Array.isArray(msgs)) {
        // Backend returns newest-first; render oldest-first for chat flow.
        setMessages([...msgs].reverse());
      }
    } catch (error) {
      // Don't clobber existing messages on failure — keep the optimistic
      // bubble visible so the agent sees what they sent.
      console.error("Whatsapp messages fetch failed:", error);
    }
  }, [integrationId, phoneE164]);

  // Fetch the integration's WhatsApp business phone number once on mount
  // to discover its `country` field. That country string is what the SSE
  // channel is keyed by on the backend (whatsapp_phone_numbers.country —
  // e.g. "India"). Without it, our subscription's channel key wouldn't
  // match what the webhook emits to, so inbound events would never arrive.
  useEffect(() => {
    if (!integrationId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchPhoneNumbers(integrationId);
        if (cancelled) return;
        const active = rows.find((r) => r.active === "Yes") || rows[0];
        if (active?.country) setIntegrationCountry(active.country);
      } catch (error) {
        console.error("Whatsapp phone-numbers fetch failed:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  // --- mount: initial load + start polling/countdown + mark read ---

  useEffect(() => {
    if (!integrationId || !phoneE164) return undefined;

    // Clear the previous conversation's state so the user doesn't see a
    // flash of stale messages / window / template selection while the new
    // phone's data loads.
    setMessages([]);
    setWindowState({ open: false, reason: "loading", remainingMs: 0 });
    setRemainingMs(0);
    setSelectedTemplateId("");
    setSamples({});

    let cancelled = false;
    (async () => {
      try {
        const list = await fetchTemplates(integrationId);
        if (!cancelled) setTemplates(Array.isArray(list) ? list : []);
      } catch (error) {
        if (!cancelled) {
          dispatch(updateSnackbar(initialSnackbar));
          dispatch(
            updateSnackbar({
              type: "error",
              message: getErrorMessage(error, "Failed to load templates"),
            }),
          );
        }
      }
      await refresh(() => cancelled);
      if (cancelled) return;
      // Fire-and-forget mark-as-read; ignore failures.
      markRead(integrationId, phoneE164).catch(() => {});
    })();

    // The drawer refreshes after each send; live updates arrive via SSE.
    // Countdown ticker is local-only (no API hits). When the 24-h window
    // ticks down to zero we also flip `windowState.open` to false so the
    // free-text composer disables itself without waiting for the next
    // backend round-trip.
    tickRef.current = setInterval(() => {
      setRemainingMs((prev) => {
        if (prev <= 0) return 0;
        const next = Math.max(0, prev - COUNTDOWN_TICK_MS);
        if (next === 0) {
          setWindowState((w) => (w.open ? { ...w, open: false, reason: "expired" } : w));
        }
        return next;
      });
    }, COUNTDOWN_TICK_MS);

    return () => {
      cancelled = true;
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [integrationId, phoneE164, refresh, dispatch]);

  // Keep the phone ref in sync with the latest valid E.164 so the SSE
  // inbound handler can filter to the conversation currently in view
  // without re-opening the EventSource on every phone edit.
  useEffect(() => {
    phoneRef.current = phoneE164;
  }, [phoneE164]);

  // --- SSE: live stream of message:inbound, message:status, template:status
  // Channel is per (integrationId, country) so we only reconnect when those
  // change. Phone-level filtering happens inside the handler via phoneRef.
  useEffect(() => {
    if (!integrationId) return undefined;
    if (typeof window === "undefined" || typeof EventSource === "undefined") {
      return undefined;
    }

    // The SSE channel is keyed by (integrationId, countryName) where
    // countryName is the EXACT string stored in whatsapp_phone_numbers.country
    // for this integration's business phone. We fetched that into
    // `integrationCountry` above. Despite the param being called `countryId`,
    // the backend treats it as a name (lowercased) when matching channels.
    if (!integrationCountry) return undefined;

    const base = process.env.REACT_APP_WHATSAPP_BASEURL;
    if (!base) return undefined;
    const url =
      `${base}/conversations/integration/${integrationId}/events` +
      `?countryId=${encodeURIComponent(integrationCountry)}`;

    const es = new EventSource(url, {
      withCredentials: true,
    });

    const safeParse = (raw) => {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    // A new inbound message arrived. If it's for the phone the user is
    // currently viewing, append it locally; also re-fetch the window
    // status because the first inbound opens the 24-h window.
    // Strip everything but digits before comparing so a `whatsapp:`
    // prefix, missing `+`, or stray whitespace in either side doesn't
    // silently drop the event.
    const digitsOnly = (p) => String(p || "").replace(/\D/g, "");

    es.addEventListener("message:inbound", (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      const current = phoneRef.current;
      if (!current || digitsOnly(data.fromPhone) !== digitsOnly(current)) return;
      if (data.message) {
        setMessages((prev) => {
          if (data.message.id && prev.some((m) => m.id === data.message.id)) {
            return prev;
          }
          return [...prev, data.message];
        });
      }
      fetchWindow(integrationId, current)
        .then((w) => {
          setWindowState(w || { open: false, reason: "unknown" });
          setRemainingMs(w?.remainingMs || 0);
        })
        .catch(() => {});
    });

    // An outbound message's delivery status changed (sent → delivered →
    // read, or failed). Patch the matching local message in place.
    es.addEventListener("message:status", (e) => {
      const data = safeParse(e.data);
      if (!data?.waMessageId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.wa_message_id === data.waMessageId
            ? { ...m, status: data.status, errorDetails: data.errorDetails }
            : m,
        ),
      );
    });

    // A template's approval state changed on Meta's side. Patch the
    // matching local template so the picker reflects the new status.
    es.addEventListener("template:status", (e) => {
      const data = safeParse(e.data);
      if (!data) return;
      const matchId = data.templateId;
      const matchWaId = data.waTemplateId;
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === matchId || t.wa_template_id === matchWaId
            ? { ...t, status: data.status, rejected_reason: data.reason }
            : t,
        ),
      );
    });

    es.onerror = (err) => {
      // EventSource auto-reconnects with exponential backoff — nothing to
      // do here besides log. If the backend is down we'll keep trying.
      console.warn("Whatsapp SSE connection issue:", err);
    };

    return () => {
      es.close();
    };
  }, [integrationId, integrationCountry]);

  // --- send handlers ---

  const handleSendText = async () => {
    const text = composerText.trim();
    if (!text || sending || !phoneE164) return;
    setSending(true);
    try {
      const sent = await sendText(integrationId, phoneE164, text);
      setComposerText("");
      // Optimistically append the new message so the chat thread updates
      // immediately, even if the follow-up refresh is slow or fails.
      if (sent) {
        setMessages((prev) => [...prev, sent]);
      }
      await refresh();
    } catch (error) {
      dispatch(updateSnackbar(initialSnackbar));
      dispatch(
        updateSnackbar({
          type: "error",
          message: getErrorMessage(error, "Failed to send message"),
        }),
      );
    } finally {
      setSending(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate || sending || !phoneE164) return;
    const missing = selectedPlaceholders.filter((k) => !samples[k]);
    if (missing.length > 0) {
      dispatch(updateSnackbar(initialSnackbar));
      dispatch(
        updateSnackbar({
          type: "error",
          message: "Please fill all template values",
        }),
      );
      return;
    }
    setSending(true);
    try {
      const sent = await sendTemplate(
        integrationId,
        phoneE164,
        buildRuntimeBody(selectedTemplate, samples),
      );
      // Reset template state first so the UI swaps out of the fill panel
      // and into the chat thread view as soon as the send resolves.
      setSelectedTemplateId("");
      setSamples({});
      setShowTemplatePickerInOpen(false);
      // Optimistically append the new outbound bubble.
      if (sent) {
        setMessages((prev) => [...prev, sent]);
      }
      await refresh();
      dispatch(updateSnackbar({ type: "success", message: "Template sent" }));
    } catch (error) {
      dispatch(updateSnackbar(initialSnackbar));
      dispatch(
        updateSnackbar({
          type: "error",
          message: getErrorMessage(error, "Failed to send template"),
        }),
      );
    } finally {
      setSending(false);
    }
  };

  // --- render helpers ---

  const dismissTemplate = () => {
    setSelectedTemplateId("");
    setSamples({});
    setShowTemplatePickerInOpen(false);
  };

  const renderTemplateSelect = () => (
    <FormControl fullWidth size="small">
      <InputLabel shrink>Template</InputLabel>
      <Select
        notched
        displayEmpty
        label="Template"
        value={selectedTemplateId}
        onChange={(e) => {
          setSelectedTemplateId(e.target.value);
          setSamples({});
        }}
        renderValue={(value) => {
          if (!value) return '';
          const t = approvedTemplates.find((x) => String(x.id) === String(value));
          return t ? t.name : '';
        }}
        MenuProps={{
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: {
            className: 'whatsapp-template-menu-paper',
            style: { maxHeight: 420 },
          },
        }}
      >
        {approvedTemplates.length === 0 && (
          <MenuItem value="" disabled>
            No approved templates available
          </MenuItem>
        )}
        {approvedTemplates.map((t) => {
          const bodyText = getBodyText(t);
          const preview =
            bodyText.length > 140 ? `${bodyText.slice(0, 140)}…` : bodyText;
          const language = t.language || '';
          const category = t.category || '';
          return (
            <MenuItem
              key={t.id}
              value={t.id}
              className="whatsapp-template-menu-item"
            >
              <div className="whatsapp-template-card-name">{t.name}</div>
              {preview && (
                <div className="whatsapp-template-card-body">{preview}</div>
              )}
              {(language || category) && (
                <div className="whatsapp-template-card-meta">
                  {language}
                  {language && category ? ' · ' : ''}
                  {category}
                </div>
              )}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );

  // The "Fill in Values for X" panel — shown above the composer when a
  // template has been chosen. Styled as a card with template metadata,
  // body preview, per-variable inputs, and a top-right X to dismiss.
  const renderTemplateFillPanel = () => {
    if (!selectedTemplate) return null;
    const bodyText = getBodyText(selectedTemplate);
    const preview =
      bodyText.length > 140 ? `${bodyText.slice(0, 140)}…` : bodyText;
    const language = selectedTemplate.language || "";
    const category = selectedTemplate.category || "";
    return (
      <div className="whatsapp-template-fill-panel">
        <div className="whatsapp-template-fill-header">
          <span className="whatsapp-template-fill-title">
            Fill in Values for {selectedTemplate.name}
          </span>
          <button
            type="button"
            className="whatsapp-template-fill-close"
            aria-label="Dismiss template"
            onClick={dismissTemplate}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="whatsapp-template-card">
          <div className="whatsapp-template-card-name">
            {selectedTemplate.name}
          </div>
          <div className="whatsapp-template-card-body">{preview}</div>
          {(language || category) && (
            <div className="whatsapp-template-card-meta">
              {language}
              {language && category ? " · " : ""}
              {category}
            </div>
          )}
        </div>

        {selectedPlaceholders.map((key) => (
          <div className="whatsapp-template-sample-row" key={key}>
            <span className="whatsapp-template-sample-label">{`Variable ${key}`}</span>
            <TextField
              size="small"
              value={samples[key] || ""}
              onChange={(e) =>
                setSamples((prev) => ({ ...prev, [key]: e.target.value }))
              }
              fullWidth
            />
          </div>
        ))}

        <div className="whatsapp-template-fill-actions">
          <Button
            variant="contained"
            size="small"
            className="whatsapp-send-btn"
            onClick={handleSendTemplate}
            disabled={
              !phoneE164 ||
              sending ||
              !selectedPlaceholders.every((k) => (samples[k] || "").length > 0)
            }
          >
            Send
          </Button>
        </div>
      </div>
    );
  };

  const composerDisabled =
    mode !== "open" || sending || !composerText.trim();

  const renderComposer = () => {
    const hasFilledTemplate =
      selectedTemplate &&
      selectedPlaceholders.every((k) => (samples[k] || "").length > 0);
    const onSendClick = selectedTemplate
      ? handleSendTemplate
      : handleSendText;
    const sendDisabled =
      !phoneE164 ||
      (selectedTemplate ? sending || !hasFilledTemplate : composerDisabled);
    return (
      <div className="whatsapp-composer">
        <div className="whatsapp-composer-input-wrap">
          <AttachFileIcon className="whatsapp-composer-attach" fontSize="small" />
          <TextField
            size="small"
            placeholder="Send Message"
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSendClick();
              }
            }}
            disabled={!!selectedTemplate || mode !== "open"}
            fullWidth
            multiline
            maxRows={4}
          />
        </div>
        <Button
          variant="contained"
          className="whatsapp-send-btn"
          onClick={onSendClick}
          disabled={sendDisabled}
        >
          Send
        </Button>
      </div>
    );
  };

  // First inbound index — used to drop the "Candidate Has Replied" banner
  // right after the candidate's first reply, mirroring the design.
  const firstInboundIndex = messages.findIndex((m) => m.direction === "inbound");

  const candidateName = applicantDetails?.firstName
    ? `${applicantDetails.firstName} ${applicantDetails.lastName || ""}`.trim()
    : applicantDetails?.name || "Candidate";

  const countdown = windowState.open ? formatRemaining(remainingMs) : null;

  // --- render ---

  if (!integrationId) {
    return (
      <div className="whatsapp-conversation whatsapp-conversation-empty">
        WhatsApp is not available for this candidate.
      </div>
    );
  }

  // Phone is intentionally NOT shown in the conversation UI — the drawer
  // is always scoped to the open applicant, and the underlying contact
  // number is read from `applicantDetails.contactNumber`. If that value is
  // missing or unparseable, Send stays disabled (see `phoneE164` checks in
  // the send handlers); we surface a single compact notice instead of the
  // earlier full "To" field so the chat panel stays clean.
  const renderPhoneIssueNotice = () => {
    const isEmpty = !rawApplicantPhone;
    const isInvalid = !isEmpty && !phoneInfo.valid;
    if (!isEmpty && !isInvalid) return null;
    const text = isEmpty
      ? "No phone number on file. Add one with country code (e.g. +91) on the applicant's profile to enable WhatsApp."
      : "Country code (e.g. +91) is mandatory. Update the applicant's phone number to send.";
    return <div className="whatsapp-phone-issue">{text}</div>;
  };

  // Idle mode: no conversation yet — the body is just the template
  // picker (dropdown + cards), no thread, no composer. Matches design #47.
  if (mode === "idle") {
    return (
      <div className="whatsapp-conversation">
        <div className="whatsapp-idle-body">
          {renderPhoneIssueNotice()}
          <div className="whatsapp-template-area">
            {renderTemplateSelect()}
            {selectedTemplate && renderTemplateFillPanel()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="whatsapp-conversation">
      {renderPhoneIssueNotice()}
      {countdown && (
         createPortal(
          <span className="whatsapp-window-banner-portal">
            (Window: {countdown})
          </span>,
          document.getElementById("sidebar-header-portal")
      ))}
      <div className="whatsapp-thread">
        {messages.map((msg, idx) => {
          const isAgent = msg.direction === "outbound";
          const avatarText = isAgent ? "ME" : initials(candidateName) || "CA";
          return (
            <React.Fragment key={msg.id || `${idx}-${msg.created_at || ""}`}>
              {idx === firstInboundIndex && (
                <div className="whatsapp-system-banner whatsapp-system-banner-success">
                  Candidate has replied — please start the conversation.
                </div>
              )}
              <div
                className={`whatsapp-message ${isAgent ? "whatsapp-message-agent" : "whatsapp-message-customer"}`}
              >
                {!isAgent && <div className="whatsapp-avatar">{avatarText}</div>}
                <div className="whatsapp-bubble">
                  <div className="whatsapp-bubble-text">
                    {(() => {
                      const { tag, body } = messageDisplay(msg);
                      return (
                        <>
                          {tag && (
                            <span className="whatsapp-template-tag-inline">
                              {tag}
                            </span>
                          )}
                          {tag && body ? " " : null}
                          {body}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {isAgent && <div className="whatsapp-avatar">{avatarText}</div>}
              </div>
            </React.Fragment>
          );
        })}

        {mode === "waiting" && (
          <div className="whatsapp-system-banner whatsapp-system-banner-info">
            Conversation will start when the candidate replies to the message.
            Please wait till there is a response from the candidate.
          </div>
        )}
      </div>

      <div className="whatsapp-footer">
        {/* Closed window: yellow notice above the picker. Free text isn't
            allowed by Meta after the 24h window, only approved templates. */}
        {mode === "closed" && (
          <div className="whatsapp-window-closed-notice">
            Window closed - only approved templates can be sent
          </div>
        )}

        {/* Template area:
            - In `closed` mode it's always open (only path to send anything).
            - In `waiting`/`open` modes it's only shown when the user toggled
              "Send template instead" so the chat thread stays clean after a
              send. */}
        {(mode === "closed" || showTemplatePickerInOpen) && (
          <div className="whatsapp-template-area">
            {renderTemplateSelect()}
            {selectedTemplate && renderTemplateFillPanel()}
          </div>
        )}

        {/* Toggle to reveal/hide the template picker in non-closed modes. */}
        {(mode === "waiting" || mode === "open") && !selectedTemplate && (
          <button
            type="button"
            className="whatsapp-template-toggle"
            onClick={() => setShowTemplatePickerInOpen((v) => !v)}
          >
            {showTemplatePickerInOpen
              ? "Hide template"
              : "Send another template"}
          </button>
        )}

        {/* Free-text composer only when 24h reply window is OPEN. In
            'closed' and 'waiting' modes the only valid path is sending an
            approved template via the picker above. */}
        {mode === "open" && !selectedTemplate && renderComposer()}
      </div>
    </div>
  );
};

export default WhatsAppConversation;
